import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTrackVersionUploadInitiated } from '@air/analytics';
import { Clips } from '@air/api';
import { type Clip } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { type DuplicateItem, DuplicateItemModal } from '~/components/Modals/DuplicateItemModal';
import { PreUploadModal } from '~/components/Modals/PreUploadModal';
import { UploadVersionsModal } from '~/components/Modals/UploadVersionsModal';
import { type OnUploadParams } from '~/components/PrivateDropzone/components/UploadDropzone';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { useProcessVisualSearchImage } from '~/components/Search/hooks/useProcessVisualSearchImage';
import { usePrivateSmartSearchSimilarToFilter } from '~/hooks/filters/usePrivateSmartSearchSimilarToFilter';
import { readPrivateUploadBoardContext } from '~/hooks/readPrivateUploadBoardContext';
import {
  splitFilesByFolder,
  type TopLevelFileUploadDecision,
  useQueuePrivateUploads,
} from '~/hooks/useQueuePrivateUploads';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useUploadValidation } from '~/hooks/useUploadValidation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { centralizedClipAssetIdSelector } from '~/store/centralizedClip/selectors';
import { type BoardsSelectOption } from '~/types/BoardSearch';
import { getClipIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';
import { resolvePrivateUploadDestination } from '~/utils/resolvePrivateUploadDestination';

export type UseUploadPrivateFilesResult = {
  uploadPrivateFiles: (params: OnUploadParams) => Promise<void>;
};

export const useUploadPrivateFiles = (): UseUploadPrivateFilesResult => {
  const store = useAirStore();
  const { showToast } = useToasts();
  const currentAssetId = useSelector(centralizedClipAssetIdSelector);
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const [showDuplicateItemModal] = useAirModal(DuplicateItemModal);
  const [showPreUploadModal] = useAirModal(PreUploadModal);
  const [showUploadVersionsModal, closeUploadVersionsModal] = useAirModal(UploadVersionsModal);
  const { trackVersionUploadInitiated } = useTrackVersionUploadInitiated();
  const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
  const { addSimilarToImageFilter } = usePrivateSmartSearchSimilarToFilter();
  const handleVisualSearchImage = useCallback(
    (base64Image: string) => {
      addSimilarToImageFilter(base64Image, 'drag-and-drop');
    },
    [addSimilarToImageFilter],
  );
  const { processVisualSearchImage } = useProcessVisualSearchImage({
    errorContext: 'PrivateDropzone: Failed to process image',
    onSearch: handleVisualSearchImage,
  });
  const { validateUpload } = useUploadValidation({ workspaceId });
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();
  const { queuePrivateUploads } = useQueuePrivateUploads();

  const uploadPrivateFiles = useCallback(
    async ({ acceptedFiles, rejectedFiles, dropZone }: OnUploadParams) => {
      if (!workspaceId) {
        return;
      }

      const {
        ancestors: currentBoardAncestors,
        boardId: currentBoardId,
        library: currentBoardLibrary,
        title: currentBoardTitle,
      } = readPrivateUploadBoardContext(
        typeof window !== 'undefined' ? window.location.pathname : '',
        store.getState(),
      );

      if (isStoragePlanRestricted) {
        showStoragePlanRestrictionModal();
        return;
      }

      const validatedFiles = await validateUpload(acceptedFiles);

      if (!validatedFiles) {
        return;
      }

      const { files, emptyFiles, filesWithNoExtension } = validatedFiles;

      let shouldCancelUpload = false;
      let selectedBoard: null | BoardsSelectOption = null;
      /**
       * `isModalVersion` is a version upload from the `VersionsUploadModal` from the AssetModal
       * as opposed to an auto-versioning prompt based on a duplicate item name or title
       */
      let isModalVersion = false;
      let assetId: Clip['assetId'] | undefined;

      const clipIdInUrl = getClipIdFromPath(window.location.pathname);

      // @deprecated This version upload logic is only used by the V1 asset modal.
      // V2 asset modal uses PrivateAssetModalDropzone instead.
      // Once V1 is fully removed, this entire clipIdInUrl section can be deleted.
      // Workspace Uploads sets dropZone to `choose-board` so the destination
      // picker runs even while an asset URL is open.
      if (clipIdInUrl && dropZone !== 'choose-board') {
        isModalVersion = await new Promise<boolean>((accept) =>
          showUploadVersionsModal({
            onUpload: () => accept(true),
            onCancel: () => {
              shouldCancelUpload = true;
              accept(false);
            },
            files,
          }),
        );

        closeUploadVersionsModal();

        if (isModalVersion) {
          assetId = currentAssetId;
          trackVersionUploadInitiated();
        }
      } else if (dropZone === 'upload' && currentBoardId) {
        if (currentBoardTitle && currentBoardAncestors) {
          selectedBoard = {
            id: currentBoardId,
            library: currentBoardLibrary,
            title: currentBoardTitle,
            ancestors: currentBoardAncestors,
          };
        }
      } else if (dropZone === 'search-by-image' && similarToImageSearchAvailable) {
        if (acceptedFiles.length !== 1) {
          showToast('Please upload a single image to search by', { color: 'red' });
          return;
        }

        await processVisualSearchImage(acceptedFiles[0]);
        return;
      } else {
        selectedBoard = await new Promise<BoardsSelectOption | null>((accept) => {
          showPreUploadModal({
            onUpload: accept,
            onCancel: () => {
              shouldCancelUpload = true;
              accept(null);
            },
            initialBoard:
              currentBoardId && currentBoardTitle && currentBoardAncestors
                ? {
                    id: currentBoardId,
                    library: currentBoardLibrary,
                    title: currentBoardTitle,
                    ancestors: currentBoardAncestors,
                  }
                : undefined,
          });
        });
      }

      if (shouldCancelUpload) {
        return;
      }

      const [filesInFolders, filesNotInFolders] = splitFilesByFolder(files);
      let duplicateHandlingData: DuplicateItem[] = [];

      if (filesNotInFolders.length) {
        const { data: matchedFilesToClips } = await Clips.matchFilesToClips({
          workspaceId,
          files: filesNotInFolders.map(({ name }) => ({ name })),
          boardId: selectedBoard?.id,
        });

        /**
         * Attach positional `index` here: `Clips.matchFilesToClips` returns results aligned
         * with `filesNotInFolders`, so `duplicateHandlingDataDict[index]` later looks up by
         * that same array position — not by an id the API returned.
         */
        const duplicateFiles = matchedFilesToClips
          .map((d, index) => ({ ...d, index }))
          .filter((item) => !!item.assetId);

        if (duplicateFiles.length > 0 && selectedBoard && !isModalVersion) {
          duplicateHandlingData = await new Promise((accept) => {
            showDuplicateItemModal({
              items: duplicateFiles,
              onComplete: accept,
              onCancel: () => {
                shouldCancelUpload = true;
                accept([]);
              },
            });
          });
        }
      }

      if (shouldCancelUpload) {
        return;
      }

      const boardToUpload = resolvePrivateUploadDestination({
        currentBoardId,
        currentBoardTitle,
        dropZone,
        selectedBoard,
      });

      const duplicateHandlingDataDict = duplicateHandlingData.reduce<Record<number, DuplicateItem>>((acc, item) => {
        acc[item.index] = item;
        return acc;
      }, {});

      const topLevelFilesToUpload = filesNotInFolders.reduce((acc: TopLevelFileUploadDecision[], file, index) => {
        const duplicateDecision = duplicateHandlingDataDict[index];

        if (duplicateDecision?.choice === 'cancel') {
          return acc;
        }

        let assetIdDestination: Clip['assetId'] | undefined;

        if (isModalVersion) {
          assetIdDestination = assetId;
        } else if (duplicateDecision?.choice === 'stack') {
          assetIdDestination = duplicateDecision.assetId;
        }

        acc.push({
          file,
          assetId: assetIdDestination,
        });

        return acc;
      }, []);

      await queuePrivateUploads({
        filesInFolders,
        topLevelFilesToUpload,
        boardToUpload,
        rejectedFiles,
        emptyFiles,
        filesWithNoExtension,
      });
    },
    [
      workspaceId,
      validateUpload,
      store,
      currentAssetId,
      showUploadVersionsModal,
      closeUploadVersionsModal,
      trackVersionUploadInitiated,
      showPreUploadModal,
      showDuplicateItemModal,
      similarToImageSearchAvailable,
      processVisualSearchImage,
      showToast,
      queuePrivateUploads,
      isStoragePlanRestricted,
      showStoragePlanRestrictionModal,
    ],
  );

  return { uploadPrivateFiles };
};
